const FALLBACK_LANGUAGE = "en";

const LOCALE_SUGGESTION_COPY = {
  fr: {
    cancel: "Non, rester sur ce site",
    close: "Fermer",
    confirm: "Oui, aller en [[country]]",
    message: "Souhaitez-vous visiter la boutique [[country]] ?",
    title: "Nous expédions en [[country]]",
  },
  de: {
    cancel: "Nein, auf dieser Website bleiben",
    close: "Schließen",
    confirm: "Ja, zu [[country]] wechseln",
    message: "Möchten Sie den Shop für [[country]] besuchen?",
    title: "Wir liefern nach [[country]]",
  },
  en: {
    cancel: "No, stay on this site",
    close: "Close",
    confirm: "Yes, go to [[country]]",
    message: "Would you like to visit the [[country]] store?",
    title: "We ship to [[country]]",
  },
  es: {
    cancel: "No, permanecer en este sitio",
    close: "Cerrar",
    confirm: "Sí, ir a [[country]]",
    message: "¿Quieres visitar la tienda de [[country]]?",
    title: "Enviamos a [[country]]",
  },
  it: {
    cancel: "No, resta su questo sito",
    close: "Chiudi",
    confirm: "Sì, vai su [[country]]",
    message: "Vuoi visitare il negozio [[country]]?",
    title: "Spediamo in [[country]]",
  },
  ja: {
    cancel: "いいえ、このサイトにとどまる",
    close: "閉じる",
    confirm: "はい、[[country]]へ移動",
    message: "[[country]]のストアをご覧になりますか？",
    title: "[[country]]へ配送しています",
  },
  nl: {
    cancel: "Nee, op deze site blijven",
    close: "Sluiten",
    confirm: "Ja, ga naar [[country]]",
    message: "Wil je de winkel voor [[country]] bezoeken?",
    title: "We leveren aan [[country]]",
  },
  pl: {
    cancel: "Nie, zostań na tej stronie",
    close: "Zamknij",
    confirm: "Tak, przejdź do [[country]]",
    message: "Czy chcesz odwiedzić sklep [[country]]?",
    title: "Dostarczamy do [[country]]",
  },
  "pt-PT": {
    cancel: "Não, ficar neste site",
    close: "Fechar",
    confirm: "Sim, ir para [[country]]",
    message: "Gostaria de visitar a loja de [[country]]?",
    title: "Enviamos para [[country]]",
  },
};

class LocaleSuggestion extends HTMLElement {
  #storageKey = "locale-suggestion:dismissed";
  #dialog;
  #cancelButton;
  #closeButtons;
  #closeIconButton;
  #confirmButton;
  #flagElement;
  #titleElement;
  #messageElement;
  #boundHandleBackdropClick;
  #boundHandleCancel;
  #boundHandleEscapeKey;
  #boundClose;
  #boundConfirm;
  #state = null;

  constructor() {
    super();

    const dialog = this.querySelector("dialog");
    const closeButtons = this.querySelectorAll(
      '[data-part="close"], [data-part="cancel"]',
    );
    const closeIconButton = this.querySelector('[data-part="close"]');
    const cancelButton = this.querySelector('[data-part="cancel"]');
    const confirmButton = this.querySelector('[data-part="confirm"]');
    const flagElement = this.querySelector('[data-part="flag"]');
    const titleElement = this.querySelector('[data-part="title"]');
    const messageElement = this.querySelector('[data-part="message"]');

    if (
      !dialog ||
      !cancelButton ||
      !closeIconButton ||
      !confirmButton ||
      !flagElement ||
      !titleElement ||
      !messageElement
    ) {
      throw new Error("LocaleSuggestion: Required elements are missing");
    }

    this.#dialog = dialog;
    this.#cancelButton = cancelButton;
    this.#closeButtons = closeButtons;
    this.#closeIconButton = closeIconButton;
    this.#confirmButton = confirmButton;
    this.#flagElement = flagElement;
    this.#titleElement = titleElement;
    this.#messageElement = messageElement;

    this.#boundHandleBackdropClick = this.#handleBackdropClick.bind(this);
    this.#boundHandleCancel = this.#handleCancel.bind(this);
    this.#boundHandleEscapeKey = this.#handleEscapeKey.bind(this);
    this.#boundClose = this.close.bind(this);
    this.#boundConfirm = this.#handleConfirm.bind(this);
  }

  connectedCallback() {
    this.#dialog.addEventListener("click", this.#boundHandleBackdropClick);
    this.#dialog.addEventListener("cancel", this.#boundHandleCancel);
    this.#dialog.addEventListener("keydown", this.#boundHandleEscapeKey);
    this.#closeButtons.forEach((button) => {
      button.addEventListener("click", this.#boundClose);
    });
    this.#confirmButton.addEventListener("click", this.#boundConfirm);

    void this.#initialize();
  }

  disconnectedCallback() {
    this.#dialog.removeEventListener("click", this.#boundHandleBackdropClick);
    this.#dialog.removeEventListener("cancel", this.#boundHandleCancel);
    this.#dialog.removeEventListener("keydown", this.#boundHandleEscapeKey);
    this.#closeButtons.forEach((button) => {
      button.removeEventListener("click", this.#boundClose);
    });
    this.#confirmButton.removeEventListener("click", this.#boundConfirm);
  }

  async #initialize() {
    const state = await this.#resolveSuggestionState();

    if (!state) {
      this.remove();
      return;
    }

    this.#state = state;
    this.#renderSuggestion(state.targetCountryName, state.detectedCountryCode);

    setTimeout(() => {
      if (!this.isConnected || this.#dialog.open) {
        return;
      }

      this.#dialog.showModal();
    }, 500);
  }

  async #resolveSuggestionState() {
    const siteCountryCode = window.Shopify.country?.toUpperCase();
    const detectedCountryCode = await this.#fetchDetectedCountryCode();
    const userLocales = this.#getUserLocales();
    const copyLanguage = this.#getCopyLanguage(userLocales);

    if (
      !siteCountryCode ||
      !detectedCountryCode ||
      detectedCountryCode === siteCountryCode
    ) {
      return null;
    }

    if (this.#isDismissed(siteCountryCode, detectedCountryCode)) {
      return null;
    }

    const form = document.getElementById("localization-selector-form");

    if (!(form instanceof HTMLFormElement)) {
      return null;
    }

    const countryInputs = Array.from(
      form.querySelectorAll('input[name="country_code"]'),
    );
    const targetCountryInput =
      countryInputs.find((input) => input.value === detectedCountryCode) ??
      null;

    if (!targetCountryInput) {
      return null;
    }

    const languages = this.#getLanguages(targetCountryInput);
    const targetLanguageCode = this.#getTargetLanguageCode(
      languages,
      userLocales,
    );

    if (!targetLanguageCode) {
      return null;
    }

    return {
      copy: LOCALE_SUGGESTION_COPY[copyLanguage],
      detectedCountryCode,
      siteCountryCode,
      targetCountryInput,
      targetCountryName: this.#getCountryDisplayName(
        detectedCountryCode,
        copyLanguage,
        targetCountryInput.dataset.countryName,
      ),
      targetLanguageCode,
    };
  }

  async #fetchDetectedCountryCode() {
    try {
      const response = await fetch(
        "https://pro.ip-api.com/json/?key=AocCUzy5bdKMEoX&fields=message,countryCode",
      );

      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      return data.countryCode?.toUpperCase() ?? null;
    } catch {
      return null;
    }
  }

  #renderSuggestion(countryName, countryCode) {
    if (!this.#state) {
      return;
    }

    this.#cancelButton.textContent = this.#state.copy.cancel;
    this.#closeIconButton.title = this.#state.copy.close;
    this.#closeIconButton.setAttribute("aria-label", this.#state.copy.close);
    this.#flagElement.replaceChildren();

    const flag = document.createElement("img");
    flag.src = `https://cdn.shopify.com/static/images/flags/${countryCode.toLowerCase()}.svg`;
    flag.alt = countryCode;
    flag.width = 24;
    flag.height = 18;
    this.#flagElement.appendChild(flag);

    this.#titleElement.textContent = this.#replaceCountryPlaceholder(
      this.#state.copy.title,
      countryName,
    );
    this.#messageElement.textContent = this.#replaceCountryPlaceholder(
      this.#state.copy.message,
      countryName,
    );
    this.#confirmButton.textContent = this.#replaceCountryPlaceholder(
      this.#state.copy.confirm,
      countryName,
    );
  }

  #replaceCountryPlaceholder(template, countryName) {
    return (template || "").replaceAll("[[country]]", countryName);
  }

  #getCountryDisplayName(countryCode, language, fallbackCountryName) {
    try {
      const displayNames = new Intl.DisplayNames([language], {
        type: "region",
      });
      return displayNames.of(countryCode) || fallbackCountryName || countryCode;
    } catch {
      return fallbackCountryName || countryCode;
    }
  }

  #getCopyLanguage(locales) {
    for (const locale of locales) {
      const exactLocale = locale?.replaceAll("_", "-");

      if (exactLocale in LOCALE_SUGGESTION_COPY) {
        return exactLocale;
      }

      const normalizedLocale = this.#normalizeLanguage(locale);

      if (normalizedLocale in LOCALE_SUGGESTION_COPY) {
        return normalizedLocale;
      }
    }

    const shopLanguage = this.#normalizeLanguage(window.Shopify.locale);
    return shopLanguage in LOCALE_SUGGESTION_COPY
      ? shopLanguage
      : FALLBACK_LANGUAGE;
  }

  #getTargetLanguageCode(languages, locales) {
    for (const locale of locales) {
      const exactMatch = languages.find(
        (language) => language.iso_code.toLowerCase() === locale.toLowerCase(),
      );

      if (exactMatch) {
        return exactMatch.iso_code;
      }

      const normalizedLocale = this.#normalizeLanguage(locale);
      const baseMatch = languages.find(
        (language) =>
          this.#normalizeLanguage(language.iso_code) === normalizedLocale,
      );

      if (baseMatch) {
        return baseMatch.iso_code;
      }
    }

    return (
      languages.find((language) => language.iso_code === window.Shopify.locale)
        ?.iso_code ?? languages[0]?.iso_code
    );
  }

  #getUserLocales() {
    const locales = Array.from(
      new Set(
        (navigator.languages?.length
          ? navigator.languages
          : [navigator.language, window.Shopify.locale]
        ).filter((locale) => Boolean(locale)),
      ),
    );

    return locales.length > 0 ? locales : [window.Shopify.locale];
  }

  #normalizeLanguage(locale) {
    return locale?.split("-")[0]?.toLowerCase() || FALLBACK_LANGUAGE;
  }

  #getLanguages(countryInput) {
    try {
      return JSON.parse(countryInput.dataset.languages || "[]");
    } catch {
      return [];
    }
  }

  #handleConfirm() {
    if (!this.#state) {
      return;
    }

    const form = document.getElementById("localization-selector-form");

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    form.querySelectorAll('input[name="country_code"]').forEach((input) => {
      input.checked = input === this.#state.targetCountryInput;
    });

    const languageSelect = document.querySelector(
      'select[name="language_code"][form="localization-selector-form"]',
    );

    if (languageSelect) {
      const languages = this.#getLanguages(this.#state.targetCountryInput);
      languageSelect.replaceChildren();

      languages.forEach((language) => {
        const option = document.createElement("option");
        option.value = language.iso_code;
        option.textContent = language.name;
        languageSelect.appendChild(option);
      });

      languageSelect.value = this.#state.targetLanguageCode;
    } else {
      const languageInput = document.createElement("input");
      languageInput.type = "hidden";
      languageInput.name = "language_code";
      languageInput.value = this.#state.targetLanguageCode;
      form.appendChild(languageInput);
    }

    form.requestSubmit();
  }

  #isDismissed(siteCountryCode, detectedCountryCode) {
    try {
      return (
        window.localStorage.getItem(
          this.#getStorageKey(siteCountryCode, detectedCountryCode),
        ) === "true"
      );
    } catch {
      return false;
    }
  }

  #rememberDismissal() {
    if (!this.#state) {
      return;
    }

    try {
      window.localStorage.setItem(
        this.#getStorageKey(
          this.#state.siteCountryCode,
          this.#state.detectedCountryCode,
        ),
        "true",
      );
    } catch {
      return;
    }
  }

  #getStorageKey(siteCountryCode, detectedCountryCode) {
    return `${this.#storageKey}:${siteCountryCode}:${detectedCountryCode}`;
  }

  #handleBackdropClick(event) {
    if (event.target === event.currentTarget && this.#dialog.open) {
      this.close();
    }
  }

  #handleCancel(event) {
    event.preventDefault();
    this.close();
  }

  #handleEscapeKey(event) {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();

    if (this.#dialog.open) {
      this.close();
    }
  }

  close() {
    this.#rememberDismissal();

    if (!this.#dialog.open) {
      this.remove();
      return;
    }

    this.#dialog.setAttribute("data-closing", "true");

    const cleanup = () => {
      this.#dialog.removeAttribute("data-closing");
      this.#dialog.close();
      this.remove();
    };

    const computedStyle = getComputedStyle(this.#dialog);
    const hasTransition =
      computedStyle.transitionDuration !== "0s" &&
      computedStyle.transitionDuration !== "";

    if (!hasTransition) {
      cleanup();
      return;
    }

    const handleTransitionEnd = (event) => {
      if (event.target === this.#dialog) {
        this.#dialog.removeEventListener("transitionend", handleTransitionEnd);
        cleanup();
      }
    };

    this.#dialog.addEventListener("transitionend", handleTransitionEnd);
  }
}

if (!window.customElements.get("locale-suggestion")) {
  window.customElements.define("locale-suggestion", LocaleSuggestion);
}
